import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PatientDeliveryAddressResponse } from '../../../api/patient-delivery-addresses';
import type { DeliveryStatus, TreatmentStage } from '../../../types/admin';
import {
  useCreateCycleDelivery,
  useUpdateCycleDelivery,
  useConfirmDeliveryReceived,
} from '../hooks/useTreatmentOps';
import { usePatientDeliveryAddresses } from '../hooks/usePatientDeliveryAddresses';
import styles from './StepPanel.module.scss';

type DeliveryPanelProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

type DeliveryAddressLike = PatientDeliveryAddressResponse | NonNullable<TreatmentStage['delivery_address']>;

function formatAddressLines(addr: DeliveryAddressLike): string {
  if (addr.shipping_method === 'nova_poshta') {
    const divisionLabel =
      addr.np_division_name && addr.np_division_number
        ? `№${addr.np_division_number}: ${addr.np_division_name}`
        : addr.np_division_name ?? null;
    const parts = [addr.np_settlement_name, divisionLabel].filter(
      (value): value is string => Boolean(value),
    );
    return parts.join(' · ');
  }
  const line1 = [addr.street, addr.house_number]
    .filter((value): value is string => Boolean(value))
    .join(', ');
  const intl = addr.shipping_method === 'international';
  const parts = [
    line1 || null,
    addr.apartment_number ? `apt. ${addr.apartment_number}` : null,
    addr.postal_code,
    intl ? addr.country : null,
  ].filter((value): value is string => Boolean(value));
  return parts.join(' · ');
}

function shippingMethodTranslationKey(
  method: DeliveryAddressLike['shipping_method'],
): 'delivery.method.novaPoshta' | 'delivery.method.ukrposhta' | 'delivery.method.international' {
  switch (method) {
    case 'nova_poshta':
      return 'delivery.method.novaPoshta';
    case 'ukrposhta':
      return 'delivery.method.ukrposhta';
    case 'international':
      return 'delivery.method.international';
  }
}

function statusToneClass(status: DeliveryStatus | null): string {
  switch (status) {
    case 'pending':
    case 'preparing_shipment':
      return styles.deliveryStatusWarning;
    case 'shipped':
      return styles.deliveryStatusInfo;
    case 'delivered':
      return styles.deliveryStatusSuccess;
    case 'cancelled':
      return styles.deliveryStatusDanger;
    default:
      return styles.deliveryStatusNeutral;
  }
}

function formatDateTime(value: string | null | undefined, locale: string): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DeliveryPanel({ patientId, treatmentId, stage }: DeliveryPanelProps) {
  const { t, i18n } = useTranslation();
  const { data: addresses = [], isLoading: addressesLoading, error: addressesError } =
    usePatientDeliveryAddresses(patientId);

  const createDeliveryMutation = useCreateCycleDelivery(patientId, treatmentId, stage.id);
  const delivery = stage.delivery ?? null;
  const deliveryId = delivery?.id ?? '';
  const updateDeliveryMutation = useUpdateCycleDelivery(patientId, treatmentId, stage.id, deliveryId);
  const confirmReceivedMutation = useConfirmDeliveryReceived(patientId, treatmentId, stage.id);

  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  const defaultPickId = useMemo(() => {
    if (stage.delivery_address?.id) {
      return stage.delivery_address.id;
    }
    const def = addresses.find((a) => a.is_default);
    return def?.id ?? addresses[0]?.id ?? '';
  }, [addresses, stage.delivery_address?.id]);

  useEffect(() => {
    setSelectedAddressId((prev) => {
      if (prev && addresses.some((a) => a.id === prev)) {
        return prev;
      }
      return defaultPickId;
    });
  }, [addresses, defaultPickId]);

  useEffect(() => {
    setTrackingNumber(delivery?.tracking_number ?? '');
  }, [delivery?.tracking_number]);

  const hasShipment = Boolean(delivery);
  const selectedAddress = addresses.find((a) => a.id === selectedAddressId) ?? null;
  const displayAddress: DeliveryAddressLike | null = hasShipment
    ? stage.delivery_address ?? selectedAddress
    : selectedAddress ?? stage.delivery_address ?? null;

  const isLoading =
    addressesLoading ||
    createDeliveryMutation.isPending ||
    updateDeliveryMutation.isPending ||
    confirmReceivedMutation.isPending;

  const savedTracking = (delivery?.tracking_number ?? '').trim();
  const trackingChanged = trackingNumber.trim() !== savedTracking;
  const receivedAt = formatDateTime(delivery?.received_confirmed_by_patient_at, i18n.language);
  const shippedAt = formatDateTime(delivery?.shipped_at, i18n.language);
  const deliveredAt = formatDateTime(delivery?.delivered_at, i18n.language);

  const statusLabel = delivery
    ? t(`status.${delivery.delivery_status}`)
    : t('delivery.notCreated');

  const addressBody = (() => {
    if (addressesLoading) {
      return <p className={styles.deliveryMuted}>{t('delivery.loadingAddresses')}</p>;
    }
    if (!displayAddress) {
      return <p className={styles.deliveryEmpty}>{t('delivery.noDeliveryAddress')}</p>;
    }
    const locationLine = [displayAddress.city, formatAddressLines(displayAddress)]
      .filter((value): value is string => Boolean(value))
      .join(' · ');
    return (
      <div className={styles.addressLines}>
        <p className={styles.addressName}>{displayAddress.recipient_name}</p>
        <p className={styles.addressPhone}>{displayAddress.recipient_phone}</p>
        <p className={styles.addressText}>{locationLine || '—'}</p>
      </div>
    );
  })();

  return (
    <div className={styles.panel}>
      <div className={styles.deliveryHeader}>
        <h4 className={styles.title}>{t('delivery.title')}</h4>
        <span className={`${styles.deliveryStatusBadge} ${statusToneClass(delivery?.delivery_status ?? null)}`}>
          {statusLabel}
        </span>
      </div>

      {!hasShipment && <p className={styles.deliveryHint}>{t('delivery.subtitle')}</p>}

      {addressesError instanceof Error && (
        <p className={styles.inlineError}>{addressesError.message}</p>
      )}

      <div className={styles.deliveryGrid}>
        <section className={styles.deliveryCard}>
          <header className={styles.deliveryCardHeader}>
            <h5 className={styles.deliveryCardTitle}>{t('delivery.addressCardTitle')}</h5>
            {displayAddress && (
              <span className={styles.methodBadge}>
                {t(shippingMethodTranslationKey(displayAddress.shipping_method))}
              </span>
            )}
          </header>

          {addressBody}

          {!hasShipment && addresses.length > 0 && (
            <div className={styles.deliveryCardFooter}>
              {addresses.length > 1 && (
                <label className={`${styles.field} ${styles.deliveryPicker}`}>
                  <span>{t('delivery.shipTo')}</span>
                  <select
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                  >
                    <option value="">{t('delivery.selectAddress')}</option>
                    {addresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.recipient_name} — {a.city || '—'} · {t(shippingMethodTranslationKey(a.shipping_method))}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={isLoading || !selectedAddressId.trim()}
                onClick={() =>
                  createDeliveryMutation.mutate({ delivery_address_id: selectedAddressId.trim() })
                }
              >
                {t('delivery.createDelivery')}
              </button>
            </div>
          )}

          {createDeliveryMutation.error instanceof Error && (
            <p className={styles.inlineError}>{createDeliveryMutation.error.message}</p>
          )}
        </section>

        {delivery && (
          <section className={styles.deliveryCard}>
            <header className={styles.deliveryCardHeader}>
              <h5 className={styles.deliveryCardTitle}>{t('delivery.shipment')}</h5>
            </header>

            <div className={styles.trackingRow}>
              <label className={`${styles.field} ${styles.trackingField}`}>
                <span>{t('delivery.trackingNumber')}</span>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder={t('delivery.enterTTH')}
                />
              </label>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={isLoading || !trackingNumber.trim() || !trackingChanged}
                onClick={() => updateDeliveryMutation.mutate({ tracking_number: trackingNumber.trim() })}
              >
                {t('delivery.save')}
              </button>
            </div>

            {updateDeliveryMutation.error instanceof Error && (
              <p className={styles.inlineError}>{updateDeliveryMutation.error.message}</p>
            )}

            {(shippedAt || deliveredAt) && (
              <div className={styles.deliveryMetaList}>
                {shippedAt && (
                  <div className={styles.deliveryMetaRow}>
                    <span className={styles.deliveryMetaLabel}>{t('delivery.shippedAt')}</span>
                    <span className={styles.deliveryMetaValue}>{shippedAt}</span>
                  </div>
                )}
                {deliveredAt && (
                  <div className={styles.deliveryMetaRow}>
                    <span className={styles.deliveryMetaLabel}>{t('delivery.deliveredAt')}</span>
                    <span className={styles.deliveryMetaValue}>{deliveredAt}</span>
                  </div>
                )}
              </div>
            )}

            {receivedAt ? (
              <p className={styles.receivedNote}>
                {t('delivery.receivedAt')} · <time>{receivedAt}</time>
              </p>
            ) : (
              <div className={styles.deliveryCardFooter}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={isLoading}
                  onClick={() => confirmReceivedMutation.mutate()}
                >
                  {t('delivery.confirmPatientReceived')}
                </button>
              </div>
            )}

            {confirmReceivedMutation.error instanceof Error && (
              <p className={styles.inlineError}>{confirmReceivedMutation.error.message}</p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
